import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  claimLabel,
  damageLabel,
  DAMAGE_AREA_OPTIONS,
  isOfferedDamageArea,
  DAMAGE_CLAIM_OPTIONS,
  DAMAGE_CLAIM_TONE,
  NO_SURVEY_LABEL,
  SURVEY_OUTCOME_OPTIONS,
  SURVEY_OUTCOME_TONE,
  SURVEY_OUTCOMES_WITH_CLAIM,
  type DamageArea,
  type DamageClaim,
  type SurveyOutcome,
  type TrackerFdfcSurveyInput,
} from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { getErrorMessage } from '@/lib/errors';
import { TONE_CLASS } from '@/lib/tone';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FOLLOW_UP_TRIGGER, TONE_ITEM, ToneLabel } from './CallStatusSelect';

/** Radix items cannot carry "", so the placeholder rows use sentinels. */
const NO_SURVEY_ITEM = '__no_survey__';
const UNDECIDED_ITEM = '__undecided__';

const asksDecision = (o: SurveyOutcome | null | '') => !!o && SURVEY_OUTCOMES_WITH_CLAIM.includes(o);

/**
 * What the person said about the kit, as a dropdown. "Received, good" saves at
 * once. "Received, damaged" and "Not received" both end at the same fork — is
 * another box going out, and what goes in it — so they open a dialog that asks,
 * and the answer is saved together with the outcome.
 *
 * `chips` (off in the list, on in the detail dialog) shows that decision beside
 * the answer; clicking a chip reopens the dialog to change it.
 *
 * Sits inside a clickable table row, so every click (including ones inside the
 * portalled dialog, which still bubble through React) stops at the wrapper.
 */
export function SurveyOutcomeSelect({
  id,
  outcome,
  damageAreas,
  damageDetail,
  damageClaim,
  chips = false,
  disabled = false,
}: {
  id: string;
  /** The newest answer on file; null = no survey yet. */
  outcome: SurveyOutcome | null;
  damageAreas?: DamageArea[] | null;
  damageDetail?: string | null;
  damageClaim?: DamageClaim | null;
  chips?: boolean;
  disabled?: boolean;
}) {
  const queryClient = useQueryClient();
  // A pick not yet confirmed by the server (or waiting in the dialog). While it
  // is set it wins over the prop, so the 30 s list poll cannot change the
  // answer under an open dialog; cleared once the saved answer is back.
  const [picked, setPicked] = useState<SurveyOutcome | null>(null);
  const value: SurveyOutcome | '' = picked ?? outcome ?? '';

  const [dialogOpen, setDialogOpen] = useState(false);
  const [claim, setClaim] = useState<DamageClaim | ''>('');
  const [areas, setAreas] = useState<DamageArea[]>([]);
  const [detail, setDetail] = useState('');

  const save = useMutation({
    mutationFn: (body: TrackerFdfcSurveyInput) => trackerFdfcService.recordSurvey(id, body),
    onSuccess: async () => {
      setDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] });
      setPicked(null);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      // In the dialog the pick stays so the user can fix and retry.
      if (!dialogOpen) setPicked(null);
    },
  });

  const openDialog = () => {
    setClaim(damageClaim ?? '');
    // Only items still offered can be re-ticked (older answers may list others).
    setAreas((damageAreas ?? []).filter(isOfferedDamageArea));
    setDetail(damageDetail ?? '');
    setDialogOpen(true);
  };

  // Closing without saving must not leave the dropdown showing an answer that
  // was never recorded.
  const cancelDialog = () => {
    setDialogOpen(false);
    setPicked(null);
  };

  const missing = value === 'not_received';
  const needsItems = claim === 'redispatch';
  const needsDetail = needsItems && areas.includes('other');
  const canSave =
    !!value && (!needsItems || areas.length > 0) && (!needsDetail || detail.trim().length > 0) && !save.isPending;

  const submitDialog = () => {
    if (!value || !canSave) return;
    save.mutate({
      outcome: value,
      damage_claim: claim || null,
      damage_areas: needsItems ? areas.filter(isOfferedDamageArea) : null,
      damage_detail: needsDetail ? detail.trim() : null,
    });
  };

  // Chips describe what is on file (props), not an unsaved pick.
  const shownDamage = damageLabel(damageAreas, damageDetail);
  const shownClaim = claimLabel(damageClaim);
  const chipItemsWord = outcome === 'not_received' ? 'send' : 'damaged';

  return (
    <span className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <Select
        // "" = no answer on file: the trigger shows the placeholder.
        value={value}
        disabled={disabled}
        onValueChange={(v) => {
          const next = v as SurveyOutcome;
          if (next === value) return;
          setPicked(next);
          if (asksDecision(next)) openDialog();
          else save.mutate({ outcome: next });
        }}
      >
        <SelectTrigger aria-label="Survey outcome" className={cn(FOLLOW_UP_TRIGGER, chips && 'w-auto')}>
          <SelectValue
            placeholder={<ToneLabel tone="neutral">{NO_SURVEY_LABEL}</ToneLabel>}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_SURVEY_ITEM} disabled className={TONE_ITEM}>
            <ToneLabel tone="neutral">{NO_SURVEY_LABEL}</ToneLabel>
          </SelectItem>
          {SURVEY_OUTCOME_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value} className={TONE_ITEM}>
              <ToneLabel tone={SURVEY_OUTCOME_TONE[o.value]}>{o.label}</ToneLabel>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* What to send — only once a re-dispatch has been agreed. */}
      {chips && asksDecision(outcome) && damageClaim === 'redispatch' && (
        <button
          type="button"
          disabled={disabled}
          onClick={openDialog}
          title={shownDamage ? `Change what to ${chipItemsWord}` : `Say what to ${chipItemsWord}`}
          className={cn(
            'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium disabled:cursor-default',
            shownDamage ? TONE_CLASS.bad : TONE_CLASS.warn,
          )}
        >
          {shownDamage ? `${chipItemsWord}: ${shownDamage}` : `what to ${chipItemsWord}?`}
        </button>
      )}

      {chips && asksDecision(outcome) && (
        <button
          type="button"
          disabled={disabled}
          onClick={openDialog}
          title={shownClaim ? 'Change what happens next' : 'Decide what happens next'}
          className={cn(
            'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium disabled:cursor-default',
            damageClaim ? TONE_CLASS[DAMAGE_CLAIM_TONE[damageClaim]] : TONE_CLASS.warn,
          )}
        >
          {shownClaim ?? 'undecided'}
        </button>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) cancelDialog(); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              submitDialog();
            }}
          >
            <DialogHeader>
              <DialogTitle>{missing ? 'Never arrived' : 'Damage report'}</DialogTitle>
              <DialogDescription>
                What happens next decides the rest: sending another box needs the list of what goes
                in it, deciding against one needs nothing but the verdict.
              </DialogDescription>
            </DialogHeader>

            <div>
              <span className="text-xs font-medium text-muted-foreground">What happens next</span>
              {/* Saving undecided is allowed: whoever itemises the damage
                  often has not spoken to the person yet. */}
              <Select
                value={claim || UNDECIDED_ITEM}
                onValueChange={(v) => setClaim(v === UNDECIDED_ITEM ? '' : (v as DamageClaim))}
              >
                <SelectTrigger aria-label="What happens next" className="mt-1 h-9 sm:h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNDECIDED_ITEM} className={TONE_ITEM}>
                    <ToneLabel tone="neutral">Not decided yet</ToneLabel>
                  </SelectItem>
                  {DAMAGE_CLAIM_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} className={TONE_ITEM}>
                      <ToneLabel tone={DAMAGE_CLAIM_TONE[o.value]}>{o.label}</ToneLabel>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {needsItems && (
              <fieldset>
                <legend className="text-xs font-medium text-muted-foreground">
                  {missing ? 'What to send' : 'What was damaged'}
                </legend>
                <div className="mt-1 grid grid-cols-2 gap-1">
                  {DAMAGE_AREA_OPTIONS.map((o) => (
                    <label
                      key={o.value}
                      className="flex items-center gap-1.5 rounded-lg border border-input px-2 py-1.5 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/10"
                    >
                      <Checkbox
                        checked={areas.includes(o.value)}
                        onChange={(e) =>
                          setAreas((cur) =>
                            e.target.checked ? [...cur, o.value] : cur.filter((a) => a !== o.value),
                          )
                        }
                      />
                      <span className="min-w-0 truncate">{o.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {needsDetail && (
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">What was it</span>
                <Input
                  className="mt-1"
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                  maxLength={300}
                  placeholder="e.g. box cover phata hua tha"
                  required
                />
              </label>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={cancelDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSave}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </span>
  );
}
